import { useState } from 'react';
import { Group, Box, Tooltip } from '@mantine/core';
import { ChevronRight, LayoutGrid } from 'lucide-react';
import type { HealthSummaryMetrics, MetricTrend, RecentSessionCard } from '../../../../types';
import { DataLibraryDrawer } from '../../../../components/DataLibrary';
import { useFeatureFlag } from '../../../../stores/featureFlags';
import classes from './CohortHealthSummary.module.css';

interface HealthSummaryHeaderProps {
  statusMessage: string;
  metrics: HealthSummaryMetrics;
  recentSessions: RecentSessionCard[];
  isNoData?: boolean;
  title?: string;
  showMetrics?: boolean;
  cohortId?: string;
}

function TrendBadge({ trend, isRating }: { trend: MetricTrend; isRating?: boolean }) {
  const className =
    trend.direction === 'up'
      ? classes.trendUp
      : trend.direction === 'down'
        ? classes.trendDown
        : classes.trendStable;

  let label: string;
  if (trend.direction === 'stable') {
    label = 'No change';
  } else if (isRating) {
    label = `${trend.direction === 'up' ? '\u2191' : '\u2193'} ${trend.change}`;
  } else {
    label = `${trend.direction === 'up' ? '\u2191' : '\u2193'} ${trend.change}%`;
  }

  // Build tooltip text
  let tooltipText = '';
  if (trend.latestValue !== null && trend.averageValue !== null) {
    if (isRating) {
      tooltipText = `Latest session (${trend.latestValue}) vs. cohort average (${trend.averageValue})`;
    } else {
      tooltipText = `Latest session (${trend.latestValue}%) vs. cohort average (${trend.averageValue}%)`;
    }
  }

  const badge = (
    <span className={`${classes.trendBadge} ${className}`}>
      {label}
    </span>
  );

  if (!tooltipText) return badge;

  return (
    <Tooltip label={tooltipText} withArrow position="bottom" color="dark">
      {badge}
    </Tooltip>
  );
}

function MetricItem({ value, label, trend, isRating, satisfactionTooltip }: {
  value: string;
  label: string;
  trend: MetricTrend;
  isRating?: boolean;
  /** MED-772: Two-line tooltip for satisfaction values */
  satisfactionTooltip?: string;
}) {
  // AC43: Don't show trend badge when insufficient data
  const showTrend = trend.latestValue !== null && trend.averageValue !== null;

  const valueElement = (
    <div
      className={classes.metricValue}
      tabIndex={satisfactionTooltip ? 0 : undefined}
      role={satisfactionTooltip ? 'note' : undefined}
      aria-label={satisfactionTooltip?.replace('\n', '; ')}
    >
      {value}
    </div>
  );

  return (
    <div className={classes.metricItem}>
      {satisfactionTooltip ? (
        <Tooltip
          label={satisfactionTooltip}
          withArrow
          position="bottom"
          color="dark"
          multiline
          w={320}
          styles={{ tooltip: { whiteSpace: 'pre-line' } }}
          events={{ hover: true, focus: true, touch: true }}
        >
          {valueElement}
        </Tooltip>
      ) : (
        valueElement
      )}
      <div className={classes.metricLabel}>{label}</div>
      {showTrend && <TrendBadge trend={trend} isRating={isRating} />}
    </div>
  );
}

function SessionCard({ session, onClick }: { session: RecentSessionCard; onClick: () => void }) {
  const statusText = session.isFlagged ? 'Needs Attention' : 'On Track';
  const dotColor = session.isFlagged ? 'var(--medley-flag-text, #E07B4C)' : '#4A7C59';

  // Format date to "MMM DD" (e.g., "Jan 29")
  const formattedDate = session.date
    ? new Date(session.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  return (
    <button
      type="button"
      className={classes.sessionCard}
      onClick={onClick}
    >
      <div className={classes.sessionCardContent}>
        <div className={classes.sessionCardTitle}>
          <span className={classes.sessionDot} style={{ backgroundColor: dotColor }} />
          {session.title}
        </div>
        <div className={classes.sessionCardMeta}>
          {formattedDate} &bull; {statusText}
        </div>
      </div>
      <ChevronRight size={16} className={classes.sessionCardArrow} />
    </button>
  );
}

const noDataTrend: MetricTrend = { direction: 'stable', change: 0, latestValue: null, averageValue: null };

export function HealthSummaryHeader({
  statusMessage,
  metrics,
  recentSessions,
  isNoData,
  title = 'Cohort Health Summary',
  showMetrics = true,
  cohortId,
}: HealthSummaryHeaderProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const dataLibraryEnabled = useFeatureFlag('data_library');

  return (
    <>
      <Box className={classes.header}>
        {/* Top row: title/message on left, stats on right */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <div className={classes.headerLabel}>{title}</div>
            <div className={classes.headerMessage}>{statusMessage}</div>
          </div>
          {showMetrics && (
            <div className={classes.metricsBar}>
              {isNoData ? (
                <>
                  <MetricItem value="—" label="Engagement" trend={noDataTrend} />
                  <MetricItem value="—" label="Attendance" trend={noDataTrend} />
                  <MetricItem value="—" label="Avg Satisfaction" trend={noDataTrend} />
                </>
              ) : (
                <>
                  {metrics.engagementAvg !== null && (
                    <MetricItem
                      value={metrics.engagementAvg.toFixed(1)}
                      label="Engagement"
                      trend={metrics.engagementTrend}
                      isRating
                    />
                  )}
                  {metrics.attendancePercent !== null && (
                    <MetricItem
                      value={`${metrics.attendancePercent}%`}
                      label="Attendance"
                      trend={metrics.attendanceTrend}
                    />
                  )}
                  {(() => {
                    // MED-772: Prefer survey-based satisfaction, fall back to session feedback rating
                    const satVal = metrics.avgSatisfaction ?? metrics.avgRating;
                    if (satVal === null) {
                      /* AC2: No satisfaction data — show dash */
                      return <MetricItem value="—" label="Avg Satisfaction" trend={noDataTrend} />;
                    }
                    return (
                      <MetricItem
                        value={
                          metrics.satisfactionTopTwoBoxPct !== null
                            ? `${satVal.toFixed(1)} (${metrics.satisfactionTopTwoBoxPct}%)`
                            : satVal.toFixed(1)
                        }
                        label="Avg Satisfaction"
                        trend={metrics.ratingTrend}
                        isRating
                        satisfactionTooltip={
                          metrics.satisfactionTopTwoBoxPct !== null
                            ? `${satVal.toFixed(1)}/5 avg satisfaction based on average rating out of 5\n${metrics.satisfactionTopTwoBoxPct}% avg satisfaction based on top-two-box analysis`
                            : undefined
                        }
                      />
                    );
                  })()}
                </>
              )}
            </div>
          )}
        </Group>

        {/* Open Data Library button */}
        {dataLibraryEnabled && (
          <button
            type="button"
            className={classes.openDataLibraryBtn}
            onClick={() => setDrawerOpen(true)}
          >
            <LayoutGrid size={14} />
            Open Data Library
          </button>
        )}

        {/* Bottom row: recent session cards */}
        {recentSessions.length > 0 ? (
          <div className={classes.sessionCardsRow}>
            {recentSessions.map((session) => (
              <SessionCard
                key={session.sessionNumber}
                session={session}
                onClick={dataLibraryEnabled ? () => setDrawerOpen(true) : () => {}}
              />
            ))}
          </div>
        ) : (
          /* AC39: No completed sessions */
          !isNoData && (
            <div className={classes.sessionCardsEmpty}>
              No sessions completed yet
            </div>
          )
        )}
      </Box>

      {/* MED-427: Data Library */}
      {dataLibraryEnabled && (
        <DataLibraryDrawer
          opened={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          defaultCohortId={cohortId}
        />
      )}
    </>
  );
}
